/* eslint-disable react/jsx-boolean-value, react/destructuring-assignment */
import React from 'react';
import PropTypes from 'prop-types';
import vapi from '../../javascript/frontend/api/vapi';
import vahoy from '../../javascript/vahoy';

import ConfirmModal from '../confirm_modal_v1';
import PitchWizard from '../pitch_wizard/PitchWizard';
import ReporterDetailsPopover from '../reporter_details_popover';

// All headshot images in assets/images/headshots, used as a stand-in avatar for
// free users (who don't get to see the real reporter). require.context is a webpack
// build-time macro, not a stable runtime API — feature-detecting it with `typeof`
// can silently resolve to the empty fallback even in real webpack bundles. Jest
// (the only environment without require.context) always sets NODE_ENV=test, so
// branch on that instead and call require.context directly everywhere else.
const headshotsContext = process.env.NODE_ENV === 'test'
   ? Object.assign(() => null, { keys: () => [] })
   : require.context('../../assets/images/headshots', false, /\.png$/);
const headshotUrls = headshotsContext.keys().map((key) => {
  const mod = headshotsContext(key);
  return mod && mod.default ? mod.default : mod;
});

function randomHeadshotUrl() {
  if (headshotUrls.length === 0) return '';
  return headshotUrls[Math.floor(Math.random() * headshotUrls.length)];
}

function BlurredReporterAvatar({
                                 url, size, initials, blurImage, rounded, objectFit, border, cornerRadius,
                               }) {
  let borderRadius = rounded ? '50%' : 0;
  if (cornerRadius !== null) {
    borderRadius = cornerRadius;
  }
  return (
    <div style={{
      width: size,
      height: size,
      borderRadius,
      border,
      overflow: 'hidden',
      // Free-plan (square) avatars are bare; the circular reporter avatars keep the backdrop.
      background: rounded ? '#EAF8FA' : 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
    >
      {url ? (
        <img
          src={url}
          alt="Reporter Avatar"
          className={`${blurImage ? 'unlock-pitch-avatar-blur' : ''}`}
          style={{
            width: '100%', height: '100%', objectFit, borderRadius,
          }}
        />
      ) : (
        <span style={{ fontSize: size / 2, color: '#bbb' }}>{initials}</span>
      )}
    </div>
  );
}

BlurredReporterAvatar.propTypes = {
  url: PropTypes.string.isRequired,
  blurImage: PropTypes.bool,
  size: PropTypes.number,
  initials: PropTypes.string,
  rounded: PropTypes.bool,
  objectFit: PropTypes.string,
  border: PropTypes.string,
  cornerRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

BlurredReporterAvatar.defaultProps = {
  blurImage: true,
  size: 80,
  initials: '',
  rounded: true,
  objectFit: 'cover',
  border: 'none',
  cornerRadius: null,
};

function anonymizedReporterLabel(outlet) {
  return outlet ? `Someone from ${outlet}` : 'Someone from the media';
}

class UnlockPitchForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.getInitialState(props);
    // Pick one random headshot per instance so it stays stable across re-renders.
    // Only free-plan users see this stand-in avatar, so skip the pick otherwise.
    this.freeHeadshotUrl = props.freePlan ? randomHeadshotUrl() : '';
  }

  getInitialState(props) {
    return {
      cancelPath: props.cancelPath,
      pitchId: props.pitchId,
      redirectPath: props.redirectPath,
      sourceRequestId: props.sourceRequestId,
      sourceRequestType: props.sourceRequestType,
      userId: props.userId,
      reporter: props.reporter,
      confirmingUnlock: false,
      restrictedFreeUser: props.restrictedFreeUser,
    };
  }

  componentDidMount() {
    const { pitchId } = this.state;
    if (pitchId) {
      this.loadSourceRequestReporter();
    }
  }

  yesToConfirmUnlock = () => {
    vahoy.track('PitchForm#yesToConfirmUnlock');
    this.createDraftPitch();
  };

  toggleConfirmDiscardModal = (freeToPitch) => {
    if (freeToPitch) {
      this.createDraftPitch();
    } else if (this.state.confirmingUnlock) {
      this.setState({ confirmingUnlock: false });
    } else {
      this.setState({ confirmingUnlock: true });
    }
  };

  loadSourceRequestReporter() {
    const params = {};
    const { sourceRequestId } = this.state;

    params.id = sourceRequestId;
    params.include = 'reporter';

    vapi.getSourceRequest(params)
      .then((response) => {
        const responseData = response.data;
        const sourceRequest = responseData.data || [];

        this.setState({
          reporter: sourceRequest.attributes.reporter,
        });
      });
  }

  createDraftPitch() {
    const { sourceRequestId } = this.state;
    const attributes = {
      draft: true,
      source_request_id: sourceRequestId,
    };
    vapi.createPitch(attributes)
      .then((response) => {
        const pitchId = response.data.id;
        this.setState({ pitchId });
        this.loadSourceRequestReporter();
      })
      .catch((error) => {
        const sourceRequestErrors = error.response.data.errors && error.response.data.errors.source_request;
        let reloaded = false;
        if (sourceRequestErrors) {
          sourceRequestErrors.forEach((value) => {
            if (value === 'has already been taken') {
              window.location.reload();
              reloaded = true;
            }
          });
        }
        if (!reloaded) {
          const message = 'Something went wrong, please try again later.';
          alert(message);
          throw error;
        }
      });
    vahoy.track('PitchForm#createDraftPitch');
  }

  renderConfirmModal() {
    const { confirmingUnlock } = this.state;
    const {
      bypassConcurrentUnlock,
      freePlan,
     } = this.props;
    return (
      <ConfirmModal
        isOpening={confirmingUnlock}
        confirmHeaderContent={<span style={{ fontWeight: 700, fontSize: '1.1rem' }}>Pitch</span>}
        confirmContent={(
          <div>
            <div style={{ fontWeight: 700, fontSize: '1.35rem', marginBottom: 8 }}>
              Ready to pitch? Use a credit to get started!
            </div>
            <div style={{ marginBottom: 8 }}>
              {freePlan ? (
                <>Using your pitch credit instantly unlocks the pitch form.</>
              ) : (
                <>Using your pitch credit instantly unlocks the reporter’s details and pitch form.</>
              )}
            </div>
            {!bypassConcurrentUnlock && (
              <div style={{ fontStyle: 'italic', color: '#6B7280', fontSize: '1rem' }}>
                Please note you’ll need to finish this pitch before using another credit.
              </div>
            )}

          </div>
        )}
        confirmNo={() => {
          vahoy.track('PitchForm#confirmModalConfirmNo');
          this.toggleConfirmDiscardModal();
        }}
        confirmNoText="Cancel"
        confirmNoButtonClassName="btn btn-outline-secondary"
        confirmYes={this.yesToConfirmUnlock}
        confirmYesText="Start Pitching"
        confirmYesButtonClassName="btn btn-primary"
        modalBodyClassName="py-4 px-4 border-0"
        modalFooterClassName="pb-4 px-4 d-flex justify-content-end gap-2 border-0 flex-row"
        className="rounded-4 border-0"
        buttonOrder="cancelFirst"
      />
    );
  }

  renderReporter() {
    const { reporter } = this.state;
    const { sourceVetted, freePlan, reporter: propsReporter } = this.props;
    // Free-plan sources never see the reporter, so this renders regardless of whether
    // state.reporter has loaded (the API returns a null reporter for free users).
    if (freePlan) {
      return (
        <>
          <div className="d-flex flex-column align-items-start">
            <BlurredReporterAvatar
              blurImage={false}
              rounded={false}
              url={this.freeHeadshotUrl}
              size={80}
              initials=""
            />
            <p className="mt-3 mb-3 w-100 text-start font-family-lato fw-normal font-size-16px" style={{ lineHeight: '20px' }}>
              <a href="/pricing_plans" className="text-coral-55">Upgrade</a>
              {' '}
              to get network benefits such as Pitch Intelligence, Media Network, Expedited Verification, and more!
            </p>
            <p className="mb-0 w-100 text-start font-family-lato fw-normal font-size-14px" style={{ lineHeight: '18px' }}>
              You will see the media name and information after they respond to your pitch.
              {' '}
              <a href="https://qwoted.com/reporter-name-visibility-update" style={{ color: '#533dd1' }}>Learn more about this change</a>
              .
            </p>
          </div>

          <div className="clearfix mb-2" />
        </>
      );
    }
    if (!sourceVetted) {
      // Unvetted/non-established sources always get a null state.reporter from the API
      // (User#source_request_show_reporter? gates on vetted_or_established? first), so this
      // reads the outlet name from props.reporter, which the ERB always populates.
      return (
        <>
          <div className="d-flex flex-column align-items-center">
            <p className="mb-0 mt-2 text-center text-black fw-bold">
              {anonymizedReporterLabel(propsReporter?.featured_employment_publication)}
            </p>
          </div>

          <div className="clearfix mb-2" />
        </>
      );
    }
    if (reporter) {
      return (
        <>
          <h6 className="text-muted mb-2 text-center">Meet the Media</h6>
          <div className="d-flex flex-column align-items-center">
            <ReporterDetailsPopover
              reporter={reporter}
              targetId="reporterInfo"
            >
              <div id="reporterInfo" style={{ cursor: 'pointer' }}>
                <BlurredReporterAvatar
                  blurImage={false}
                  url={reporter.avatar_url}
                  size={80}
                  initials={reporter.initials || ''}
                />
                <div>
                  <p className="mb-0 mt-2">
                    <span className="fw-bold mt-2 text-primary">
                      {reporter.name}
                    </span>
                  </p>

                  <div className="card-grid-subtitle fw-bold font-size-12px">
                    {reporter.employment_string}
                  </div>
                </div>
              </div>
            </ReporterDetailsPopover>
          </div>

          <div className="clearfix mb-2" />
        </>
      );
    }
  }

  renderPitchForm() {
    const { pitchId } = this.state;
    const {
      sourceRequestId,
      cancelPath,
      profileType,
      redirectPath,
      userId,
      conversationOnly,
      restrictedFreeUser,
      sourceRequestType,
      opportunityDetails,
      videoPitchRecordingEnabled,
      aiDetectorEnabled,
    } = this.props;
    const welcome = sourceRequestType === 'welcome';
    return (
      <div className="row">
        {!welcome && (
          <div className="col-md-4 mt-3">
            {this.renderReporter()}
          </div>
        )}
        <div className={welcome ? 'col-md-12' : 'col-md-8'}>
          <PitchWizard
            cancelPath={cancelPath}
            conversationOnly={conversationOnly}
            pitchId={pitchId}
            redirectPath={redirectPath}
            requestId={sourceRequestId}
            userId={userId}
            profileType={profileType}
            restrictedFreeUser={restrictedFreeUser}
            sourceRequestType={sourceRequestType}
            opportunityDetails={opportunityDetails}
            videoPitchRecordingEnabled={videoPitchRecordingEnabled}
            aiDetectorEnabled={aiDetectorEnabled}
          />
        </div>
      </div>
    );
  }

  renderUnlockAvatar() {
    const { reporter, sourceVetted, freePlan } = this.props;
    if (freePlan) {
      if (reporter?.featured_employment_publication_logo_url) {
        return (
          <BlurredReporterAvatar
            blurImage={false}
            rounded={false}
            objectFit="contain"
            border="1px solid #dde1e5"
            cornerRadius={4}
            url={reporter.featured_employment_publication_logo_url}
            size={80}
            initials=""
          />
        );
      }
      return (
        <span className="text-black fw-bold">
          {reporter?.featured_employment_publication || 'The media'}
        </span>
      );
    }
    if (!sourceVetted) {
      return (
        <span className="text-black fw-bold">
          {anonymizedReporterLabel(reporter?.featured_employment_publication)}
        </span>
      );
    }
    return (
      <BlurredReporterAvatar
        url={reporter?.avatar_url}
        size={80}
        initials={reporter?.initials || ''}
      />
    );
  }

  renderUnlockMessage() {
    const {
      unlimitedPitches,
      freeToPitch,
      conversationOnly,
      pitchesRemaining,
      sourceRequestType,
      pitchRateLimited,
      freePlan,
    } = this.props;

    if (pitchesRemaining > 0 && !unlimitedPitches && !freeToPitch) {
      return (
        <div className="d-flex justify-content-between align-items-start my-3" style={{ gap: '1.5rem' }}>
          <div className="me-3 flex-shrink-0">
            {this.renderUnlockAvatar()}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between" style={{ gap: '0.75rem' }}>
              <span className="fw-bold" style={{ fontSize: '0.95rem', display: 'flex', alignItems: 'center' }}>
                <i className="fa-solid fa-sparkles me-1" style={{ color: '#3E1FB7', fontSize: '1rem' }} />
                <span style={{ fontSize: '1rem' }}>Your next big opportunity could be a pitch away!</span>
              </span>
            </div>
            <div className="mb-3" style={{ color: '#555', fontSize: '1rem' }}>
              {freePlan ? (
                <>
                  You will see the media name and information after they respond to your pitch.
                  {' '}
                  <a href="https://qwoted.com/reporter-name-visibility-update">Learn more about this change.</a>
                </>
              ) : (
                <>Using a pitch credit will reveal details about the opportunity's media contact.</>
              )}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => this.toggleConfirmDiscardModal(freeToPitch || conversationOnly)}
              type="button"
              disabled={pitchRateLimited}
            >
              Pitch Now
            </button>
          </div>
          {this.renderConfirmModal()}
        </div>
      );
    }

    if (pitchesRemaining === 0 && !freeToPitch && !unlimitedPitches) {
      return (
        <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: '1.5rem' }}>
          <div className="me-3 flex-shrink-0">
            {this.renderUnlockAvatar()}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between mb-2" style={{ gap: '0.75rem' }}>
              <span
                className="fw-bold"
                style={{
                  color: '#3E1FB7', fontSize: '0.95rem', display: 'flex', alignItems: 'center',
                }}
              >
                <i className="fa-solid fa-sparkles me-1" style={{ color: '#3E1FB7', fontSize: '1rem' }} />
                Your next big opportunity could be a pitch away!
              </span>
            </div>
            <div className="mb-3" style={{ color: '#555', fontSize: '1rem' }}>Upgrade your plan to get more credits and
              keep pitching.
            </div>
          </div>
        </div>
      );
    }

    if (freeToPitch && !unlimitedPitches) {
      return (
        <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: '1.5rem' }}>
          {sourceRequestType != 'welcome' && (
            <div className="me-3 flex-shrink-0">
              {this.renderUnlockAvatar()}
            </div>
          )}
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between mb-2" style={{ gap: '0.75rem' }}>
              <span
                className="fw-bold"
                style={{
                  color: '#3E1FB7', fontSize: '0.95rem', display: 'flex', alignItems: 'center',
                }}
              >
                <i className="fa-solid fa-sparkles me-1" style={{ color: '#3E1FB7', fontSize: '1rem' }} />
                Your next big opportunity could be a pitch away!
              </span>
            </div>
            <div className="mb-3" style={{ color: '#555', fontSize: '1rem' }}>
              {sourceRequestType === 'welcome' ? (
                <>It is <span className="fw-bold">free</span> to make an introduction to the media.</>
              ) : (
                <>Pitch now without using your credits.</>
              )}
            </div>

            <button
              className="btn btn-primary"
              onClick={() => this.toggleConfirmDiscardModal(true)}
              type="button"
              disabled={pitchRateLimited}
            >
              {sourceRequestType === 'welcome' ? (
                <>Introduce Yourself or a Source</>
              ) : (
                <>Pitch now</>
              )}
            </button>
          </div>
          {this.renderConfirmModal()}
        </div>
      );
    }

    if (conversationOnly && !unlimitedPitches) {
      return (
        <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: '1.5rem' }}>
          <div className="me-3 flex-shrink-0">
            {this.renderUnlockAvatar()}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between mb-2" style={{ gap: '0.75rem' }}>
              <span
                className="fw-bold"
                style={{
                  color: '#3E1FB7', fontSize: '0.95rem', display: 'flex', alignItems: 'center',
                }}
              >
                <i className="fa-solid fa-sparkles me-1" style={{ color: '#3E1FB7', fontSize: '1rem' }} />
                Your next big opportunity could be a pitch away!
              </span>
            </div>
            <div className="mb-2">
              <span className="fw-bold" style={{ color: '#3E1FB7' }}>Start a conversation for free!</span>
            </div>
            <div className="mb-3" style={{ color: '#555', fontSize: '1rem' }}>No pitch credits will be used.</div>
            <button
              className="unlock-pitch-btn-purple"
              style={{ padding: '0.5rem 1.5rem' }}
              onClick={() => this.toggleConfirmDiscardModal(true)}
              type="button"
            >
              Start Conversation
            </button>
          </div>
          {this.renderConfirmModal()}
        </div>
      );
    }

    if (unlimitedPitches) {
      return (
        <div className="d-flex justify-content-between align-items-start mb-3" style={{ gap: '1.5rem' }}>
          <div className="me-3 flex-shrink-0">
            {this.renderUnlockAvatar()}
          </div>
          <div className="flex-grow-1">
            <div className="d-flex justify-content-between mb-2" style={{ gap: '0.75rem' }}>
              <span
                className="fw-bold"
                style={{
                  color: '#3E1FB7', fontSize: '0.95rem', display: 'flex', alignItems: 'center',
                }}
              >
                <i className="fa-solid fa-sparkles me-1" style={{ color: '#3E1FB7', fontSize: '1rem' }} />
                Your next big opportunity could be a pitch away!
              </span>
            </div>
            <div className="mb-3" style={{ color: '#555', fontSize: '1rem' }}>Using a pitch credit will reveal details about the opportunity's media contact.</div>
            <button
              className="btn btn-primary"
              onClick={() => this.toggleConfirmDiscardModal(true)}
              type="button"
              disabled={pitchRateLimited}
            >
              Pitch Now
            </button>
          </div>
          {this.renderConfirmModal()}
        </div>
      );
    }

    // fallback (should not occur)
    return null;
  }

  render() {
    const { pitchId } = this.state;
    if (pitchId) {
      return this.renderPitchForm();
    }
    return this.renderUnlockMessage();
  }
}

UnlockPitchForm.propTypes = {
  pitchesRemaining: PropTypes.number,
  cancelPath: PropTypes.string.isRequired,
  unlimitedPitches: PropTypes.bool,
  freeToPitch: PropTypes.bool,
  pitchId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
  redirectPath: PropTypes.string,
  sourceRequestId: PropTypes.number.isRequired,
  sourceRequestType: PropTypes.string,
  userId: PropTypes.number.isRequired,
  bypassConcurrentUnlock: PropTypes.bool,
  conversationOnly: PropTypes.bool,
  reporter: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
  profileType: PropTypes.string.isRequired,
  restrictedFreeUser: PropTypes.bool,
  sourceVetted: PropTypes.bool,
  pitchRateLimited: PropTypes.bool,
  freePlan: PropTypes.bool,
  opportunityDetails: PropTypes.shape({
    title: PropTypes.string,
    details: PropTypes.string,
  }),
  videoPitchRecordingEnabled: PropTypes.bool,
  aiDetectorEnabled: PropTypes.bool,
};

UnlockPitchForm.defaultProps = {
  pitchesRemaining: undefined,
  pitchId: undefined,
  freeToPitch: false,
  unlimitedPitches: false,
  redirectPath: '/source_requests',
  bypassConcurrentUnlock: false,
  conversationOnly: false,
  reporter: undefined,
  restrictedFreeUser: false,
  sourceRequestType: '',
  sourceVetted: false,
  pitchRateLimited: false,
  freePlan: false,
  opportunityDetails: null,
  videoPitchRecordingEnabled: false,
  aiDetectorEnabled: false,
};

export default UnlockPitchForm;
